import { useEffect } from 'react';

const SITE_ORIGIN = 'https://exterminatorproservice.com';

/**
 * Lightweight SEO manager for SPA routes.
 * Updates <title>, meta description, canonical link, og:url, og:title, og:description,
 * and injects a per-page JSON-LD block. Reverts on unmount.
 */
export function useSEO({ title, description, canonicalPath, jsonLd }) {
  useEffect(() => {
    const prevTitle = document.title;

    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrValue] = selector
          .replace('meta[', '')
          .replace(']', '')
          .split('=');
        el.setAttribute(attrName, attrValue.replace(/["']/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return el;
    };

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }

    if (canonicalPath) {
      const absolute = `${SITE_ORIGIN}${canonicalPath}`;
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', absolute);
      setMeta('meta[property="og:url"]', 'content', absolute);
      setMeta('meta[name="twitter:url"]', 'content', absolute);
    }

    const injectedScripts = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-dynamic-seo', 'true');
        script.text = JSON.stringify(block);
        document.head.appendChild(script);
        injectedScripts.push(script);
      });
    }

    return () => {
      document.title = prevTitle;
      injectedScripts.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, [title, description, canonicalPath, jsonLd]);
}
